/*eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
import React, { type FC, type RefObject, useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { usePlaylistAutoPlay } from '../../hooks/usePlaylistAutoPlay';
import type { ModifyPlaylistButtons_Playlist$key } from '../playlist/__generated__/ModifyPlaylistButtons_Playlist.graphql';
import type { PlaylistItemData } from '../playlist/PlaylistItem';

type AutoPlayManagerProps = {
	cloudId: string;
	audioElementRef: RefObject<HTMLAudioElement>;
	children: (playlistRef: ModifyPlaylistButtons_Playlist$key) => React.ReactNode;
};

export const AutoPlayManager: FC<AutoPlayManagerProps> = ({
	cloudId,
	audioElementRef,
	children,
}) => {
	const { activeContent } = useAudioPlayerStore();

	//Atlassian rules are that fragment spreads can't be split between modules, however
	//these are currently only split between folders in the same module?
	//since modifyPlaylistButton may exist outside the module it may make sense to have a full copy
	// of this query inside that component as lint desires?
	const data = useLazyLoadQuery<any>(
		graphql`
			query AutoPlayManagerPlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								space {
									key
								}
								history {
									createdBy {
										displayName
									}
								}
							}
							supportingSources
						}
					}
				}
				...ModifyPlaylistButtons_Playlist @arguments(cloudId: $cloudId)
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'store-or-network',
		},
	);

	const playlistItems: PlaylistItemData[] = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		return tracks.map((track: any) => {
			const mainContent = track.mainContent;
			const creator = mainContent?.history?.createdBy;
			const supportingSources = track.supportingSources || [];

			return {
				id: mainContent?.id,
				title: mainContent?.title || undefined,
				type: track.type,
				author: creator?.displayName || undefined,
				spaceKey: mainContent?.space?.key || '',
				contentType: mainContent?.type || '',
				contentId: mainContent?.id,
				mainSource: track.mainSource,
				supportingSources: supportingSources,
			};
		});
	}, [data]);

	// Enable auto-play of next track when current track ends
	usePlaylistAutoPlay({
		audioRef: audioElementRef,
		playlistItems,
		currentItemId: activeContent?.contentId,
	});

	return <>{children(data)}</>;
};
